import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, IconButton, SelectChangeEvent, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ShepherdTourContext } from 'react-shepherd';
import {
  ICandidate,
  IJobCandidateInteraction,
  amplitude,
  getSubmissionSettings,
  ISubmissionSettingsResponse,
  ICandidateRecentConversation,
} from 'wisestep-api';
import {
  WsListingLoader,
  WsPagination,
  formattedDateISO,
  WsFilterTags,
  IFilterList,
  WsTitleBanner,
  WsColors,
} from 'wisestep-base';

import BulkEmail from '../../../components/BulkEmail';
import BulkSmsDrawer from '../../../components/BulkSms/BulkSmsDrawer';
import { workAuthorization } from '../../../components/filters/components/AdvanceFilterHelper';
import MappingsAdvanceFilterFormProps from '../../../components/filters/models/MappingAdvanceFiltersFormProps';
import NoDataFoundCard from '../../../components/noDataFoundCard';
import { useWorkspaceContext } from '../../../context';
import CandidateBulkSubmissions from '../../../features/conversationWidget/components/BulkSubmission/CandidateBulkSubmissions';
import conversationWidgetStore from '../../../features/conversationWidget/store/conversationWidgetStore';
import { ITrackCvPages } from '../../../features/conversationWidget/utils/cvHelper';
import { handleCandidateDownload } from '../../../features/dataDownload/helper/PayloadHelper';
// import NoDataFoundUploadCandidate from '../../../features/importCandidates/components/noDataFoundUploadCandidate';
import ShareCandidateProfileEmail from '../../../features/submissions/submissionListing/components/ShareCandidateProfileEmail';
import ErrorLogger from '../../../providers/ErrorLogger';
import { getCachedJob } from '../../../utils/cacheService';
import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { paginationSettingsStore } from '../../../utils/PaginationSettingStore';
import { extractEmail } from '../../../utils/StringUtils';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { handleSelectedCandidatesLimit } from '../../jobsCrm/components/helper/SendSmsEmailFuntionality';
import useListingCandidates from '../../submissions/hooks/useListingCandidates';
import ActionsFiltersWithTitle from '../components/actionsFiltersWithTitle';
import CandidateBulkSelectionBar from '../components/candidateBulkSelectionBar';
import { TalentPoolSearchRequest } from '../talentpool/components/search/TalentPoolSearchRequest';
import TalentPoolListSideNav from '../talentpool/components/talentpoolListSideNav';
import {
  AppLayoutTheme,
  getInteraction,
  LayoutTheme,
  pushMappingFilters,
  setDefaultCandidateSearchFilters,
  setDefaultCandidateAdvanceFilters,
  Pipeline_Implementation_message,
} from '../utils/CandidateHelper';

import MappingCandidateCard from './components/mappingCard';
import MappingsSearch from './components/search';
import { MappingListDefaultRequest } from './MappingListDefaultRequest';

type mappingParams = {
  source?: string;
  encoded?: string;
};

const MappingListing = () => {
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const job = getCachedJob();
  const tour = useContext(ShepherdTourContext);
  const workspace = useWorkspaceContext();
  const navigateBack = useNavigate();
  const params = useParams<mappingParams>();
  const defaultQuery = { ...MappingListDefaultRequest };
  const [paramsByQuery] = useSearchParams();
  const setSubmissionSettings = async () => {
    const setting = await getSubmissionSettings();
    setSubmissionSetting(setting.data);
  };
  const [submissionSetting, setSubmissionSetting] = useState<ISubmissionSettingsResponse | null>(
    null
  );
  const [openSubmitCandidate, setOpenSubmitCandidate] = useState(false);

  if (params.encoded && params.source == 'similarJobs') {
    const similarJobsData = JSON.parse(decodeURI(params.encoded));
    defaultQuery.auditQuery = false;
    defaultQuery.source = 'Similar Jobs';
    defaultQuery.candidateSearchType = 'All';
    defaultQuery.targetIndex = 'SG';
    defaultQuery.targetIndex = '';
    if (similarJobsData.jobIds.length == 1) {
      defaultQuery.jobId = similarJobsData.jobIds.toString();
    } else {
      defaultQuery.jobId = '';
      defaultQuery.jobIds = similarJobsData.jobIds.toString();
    }
    defaultQuery.jobCode = similarJobsData.jobCodes.toString();
  }
  if (paramsByQuery.get('email')) {
    defaultQuery.candidateEmail = String(paramsByQuery.get('email'));
  } else {
    defaultQuery.candidateEmail = '';
  }
  defaultQuery.batchId = paramsByQuery.get('batchId') ? String(paramsByQuery.get('batchId')) : '';
  defaultQuery.fileId = paramsByQuery.get('fileId') ? String(paramsByQuery.get('fileId')) : '';
  const [searchParams, setSearchParams] = useSearchParams();
  const queryData = searchParams.get('d') ?? '';
  const [query, setQuery] = useState<IJobCandidateInteraction>(
    queryData ? getDecodedQueryParams(queryData) : defaultQuery
  );
  const [pageNumber, setPageNumber] = useState<number>(query.page ?? 0 + 1 ?? 1);
  const [loader, setLoader] = useState<boolean>(false);
  const [tempPageValue, setTempPageValue] = useState<number>(0);

  const [openShareCandidateEmail, setOpenShareCandidateEmail] = useState<boolean>(false);
  const [candidateToShare, setCandidateToShare] = useState<ICandidate | null>();
  const [selectedCandidateEntitys, setSelectedCandidateEntitys] = useState<
    ICandidateRecentConversation[]
  >([]);

  const paginationData = paginationSettingsStore((state) => state.getPaginationSettings);
  const paginationSettings = paginationData();
  const candidatesData = useListingCandidates(query, setLoader, 'candidate');
  const selectedIndex = (index: number, value: string) => console.log(index, value);
  const [cvIndex, setCvIndex] = useState<number | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>([]);
  const [advancedFilterDefaultValues, setAdvancedFilterFields] = useState<
    Partial<MappingsAdvanceFilterFormProps>
  >(setDefaultCandidateAdvanceFilters(query) ?? {});
  const [searchFilterDefaultValues, setSearchFilterDefaultValues] =
    useState<TalentPoolSearchRequest>(setDefaultCandidateSearchFilters(query) ?? {});

  const [numberOfSelectedCandidates, setNumberOfSelectedCandidates] = useState<number>(0);
  const openModal = useConversationWidgetStore((state) => state.setCandidate);
  const navigate = useConversationWidgetStore((state) => state.navigate);
  const navigationState = useConversationWidgetStore((state) => state.navigationState);
  const updateNavigationState = useConversationWidgetStore((state) => state.updateNavigationState);
  const setIsWidgetOpen = useConversationWidgetStore((state) => state.setIsWidgetOpen);
  const [openBulkSms, setOpenBulkSms] = useState<boolean>(false);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const setSelectedEmail = useConversationWidgetStore((state) => state.updateSelectedEmail);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);

  useEffect(() => {
    if (advancedFilterDefaultValues)
      setAppliedFilters(pushMappingFilters(advancedFilterDefaultValues));

    if (navigationState && navigate && cvIndex !== null) {
      if (cvIndex > 0 && cvIndex <= candidatesData.list.length - 1 && navigate == 'prev') {
        const index = cvIndex - 1;
        setCvIndex(index);
        // openModal(candidatesData.list[index]);
      } else if (cvIndex >= 0 && cvIndex < candidatesData.list.length - 1 && navigate == 'next') {
        const index = cvIndex + 1;
        setCvIndex(index);
        // openModal(candidatesData.list[index]);
      } else {
        setIsWidgetOpen(false);
      }
      updateNavigationState(false);
    }
  }, [
    advancedFilterDefaultValues,
    candidatesData.list,
    cvIndex,
    navigate,
    navigationState,
    openModal,
    setIsWidgetOpen,
    updateNavigationState,
  ]);

  const pageChange = (event, value) => {
    amplitude(value < tempPageValue ? 'Pipeline_Navigation_Previous' : 'Pipeline_Navigation_Next', {
      clickedOn: value < tempPageValue ? 'Previous Button' : 'Next Button',
      workspaceName: workspace.name,
      pageName: 'Mappings',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });

    setTempPageValue(value);
    if (value > 9) {
      setNotification(
        warning(
          'We are showing maximum of 500 conversations, Refer to Talent Pool to review your candidates '
        )
      );
    } else {
      unselectAllCandidates();
      const updatedQuery = { ...query };
      updatedQuery.page = value;
      setSearchParams({ d: getEncodedQueryParams(updatedQuery) });
      setQuery(updatedQuery);
      setPageNumber(value + 1);
    }
  };

  const search = (data: TalentPoolSearchRequest) => {
    setSearchFilterDefaultValues(data);
    const updatedQuery = { ...query };
    updatedQuery.page = 0;
    updatedQuery.query = data.query;
    updatedQuery.candidateLocations = data.locations;
    updatedQuery.radiusInMiles = data.distance;
    updatedQuery.searchScope = data.searchScope;
    if (data.excludeDuplicateRecords == 'true') {
      updatedQuery.excludeDuplicateRecords = true;
    } else if (data.excludeDuplicateRecords == 'false') {
      updatedQuery.excludeDuplicateRecords = false;
    }
    if (data.candidateLocationPref === 'Current') {
      updatedQuery.searchInCurrentLocation = true;
      updatedQuery.searchInPreferredLocation = false;
    } else if (data.candidateLocationPref === 'Preferred') {
      updatedQuery.searchInCurrentLocation = false;
      updatedQuery.searchInPreferredLocation = true;
    } else {
      updatedQuery.searchInCurrentLocation = true;
      updatedQuery.searchInPreferredLocation = true;
    }
    // if (data.query !== undefined && data.query.length > 0) {
    //   const srt = data.sort ? data.sort.split(',') : ['desc'];
    //   updatedQuery.sort = [`_score,${srt[1] ?? 'desc'}`];
    // } else {
    //   updatedQuery.sort = [data.sort ?? 'createdOn,desc'];
    // }
    updatedQuery.recruiterActions = data.recruiterActions;
    updatedQuery.candidateResponses = data.candidateResponses;
    updatedQuery.searchWithMostRecentConversation = data.searchWithMostRecentConversation;
    if (params.encoded && params.source == 'similarJobs') {
      const similarJobsData = JSON.parse(decodeURI(params.encoded));
      updatedQuery.auditQuery = false;
      updatedQuery.source = 'Similar Jobs';
      updatedQuery.candidateSearchType = 'All';
      updatedQuery.targetIndex = 'SG';
      updatedQuery.targetIndex = '';
      if (similarJobsData.jobIds.length == 1) {
        updatedQuery.jobId = similarJobsData.jobIds.toString();
      } else {
        updatedQuery.jobId = similarJobsData.jobIds[0].toString();
        updatedQuery.jobIds = similarJobsData.jobIds.toString();
      }
      updatedQuery.jobCode = similarJobsData.jobCodes.toString();
      updatedQuery.candidateSearchType = 'MappingsToSimilarJobs';
    }
    setSearchParams({ d: getEncodedQueryParams(updatedQuery) });
    setPageNumber(1);
    setQuery(updatedQuery);
  };

  const onDeleteFilterTag = (tag: string) => {
    setAppliedFilters((prev) => {
      const chipId = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const advancedFilterDefaults: Partial<MappingsAdvanceFilterFormProps> =
        advancedFilterDefaultValues;

      if (chipId == 'totalYearsExperienceStartRange' || chipId == 'totalYearsExperienceEndRange') {
        advancedFilterDefaults['totalYearsExperienceStartRange'] = 0;
        advancedFilterDefaults['totalYearsExperienceEndRange'] = 0;
        advancedFilterDefaultValues['experienceComparisonOperator'] = 'isBetween';
      } else if (['jobStatus', 'jobType'].includes(chipId)) advancedFilterDefaults[chipId] = null;
      else advancedFilterDefaults[chipId] = [];
      setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
      setSearchParams({ d: getEncodedQueryParams(updateQuery(advancedFilterDefaults)) });
      setQuery(updateQuery(advancedFilterDefaults));
      const index = prev.findIndex((existChip) => existChip.id === chipId);
      prev.splice(index, 1);
      return [...prev];
    });
  };
  const updateQuery = (data: MappingsAdvanceFilterFormProps) => {
    const updatedQuery = { ...query };
    ['emailAvailability', 'phoneAvailability'].forEach((field) => delete updatedQuery[field]);
    updatedQuery.page = 0;
    updatedQuery.jobStatus = data.jobStatus ?? null;
    updatedQuery.fromDateTime = data.createBWDates?.fromDateName
      ? formattedDateISO(data.createBWDates?.fromDateName)
      : '';
    updatedQuery.toDateTime = data.createBWDates?.toDateName
      ? formattedDateISO(data.createBWDates?.toDateName)
      : '';
    if (updatedQuery.fromDateTime != null && !isEmpty(updatedQuery.fromDateTime)) {
      updatedQuery.fromDateTime = new Date(updatedQuery.fromDateTime);
      updatedQuery.fromDateTime.setHours(0, 0, 0);
      updatedQuery.fromDateTime = formattedDateISO(updatedQuery.fromDateTime);
    }
    if (updatedQuery.toDateTime != null && !isEmpty(updatedQuery.toDateTime)) {
      updatedQuery.toDateTime = new Date(updatedQuery.toDateTime);
      updatedQuery.toDateTime.setHours(23, 59, 59);
      updatedQuery.toDateTime = formattedDateISO(updatedQuery.toDateTime);
    }
    updatedQuery.jobAccountManagerEmails =
      data.accountManagers?.map((email) => extractEmail(email)) ?? [];
    updatedQuery.jobClientRecruiterEmails = data.clientRecruiters;
    updatedQuery.submittedByRecruiterIds = data.recruiters;
    updatedQuery.clientNames = data.clients;
    updatedQuery.currentStageCurrentStatusPairs = data.stageStatus;
    updatedQuery.searchAcrossTeams = [];
    data.teams?.forEach((obj) => updatedQuery.searchAcrossTeams?.push(obj.id ?? ''));
    updatedQuery.candidateSearchTypeList = [];
    data.candidateStatus?.forEach((obj) =>
      updatedQuery.candidateSearchTypeList?.push(obj.toLowerCase())
    );
    data.contactAvailability?.forEach((obj) => {
      if (obj == 'Email') updatedQuery.emailAvailability = true;
      else if (obj == 'Phone') updatedQuery.phoneAvailability = true;
      else if (obj == 'Both') {
        updatedQuery.phoneAvailability = true;
        updatedQuery.emailAvailability = true;
      }
    });
    updatedQuery.jobTitles = data.jobTitles;
    updatedQuery.workAuthorizationCategories = [];
    data.workAuthorization?.forEach((obj) =>
      updatedQuery.workAuthorizationCategories?.push(workAuthorization[obj])
    );
    data.commPreferences?.forEach((value) => {
      if (value == 'Email') updatedQuery.commPreferenceEmail = true;
      else if (value == 'Phone') updatedQuery.commPreferencePhone = true;
    });

    updatedQuery.currentEmployers = data.currentEmployers;
    updatedQuery.employers = data.previousEmployers ?? [];
    updatedQuery.minSalary = data.salaryRange?.min ?? 0;
    updatedQuery.maxSalary = data.salaryRange?.max ?? 0;
    updatedQuery.salaryCurrency = data.salaryRange?.currency ?? null;
    updatedQuery.salaryTimeSpan = data.salaryRange?.unitTime ?? null;
    updatedQuery.noticePeriodMaxDays = data.noticePeriod?.max ?? 0;
    updatedQuery.noticePeriodMinDays = data.noticePeriod?.min ?? 0;
    updatedQuery.languages = data.languages ?? [];
    updatedQuery.jobType = data.jobType ?? null;
    data.jobPreference?.forEach((obj) => {
      if (obj == 'Contract') updatedQuery.jobPreferenceContract = true;
      else if (obj == 'Permanent') updatedQuery.jobPreferencePermanent = true;
    });
    updatedQuery.searchWithMostRecentConversation = data.searchWithMostRecentConversation;
    updatedQuery.tags = data.tags ?? [];
    updatedQuery.sources = data.source;
    updatedQuery.experienceComparisonOperator = data?.experienceComparisonOperator ?? 'isBetween';
    updatedQuery.totalYearsExperienceStartRange = data.totalYearsExperienceStartRange ?? 0;
    updatedQuery.totalYearsExperienceEndRange = data.totalYearsExperienceEndRange ?? 0;
    updatedQuery.searchScope = data.searchScope;
    return updatedQuery;
  };
  const onApplyFilters = (data: MappingsAdvanceFilterFormProps) => {
    setSearchParams({ d: getEncodedQueryParams(updateQuery(data)) });
    setQuery(updateQuery(data));
    setPageNumber(1);
    setAdvancedFilterFields(data);
    setAppliedFilters(pushMappingFilters(data));
  };
  const setNotification = useNotificationStore((state) => state.setNotification);
  const handleDownload = async (type: string) => {
    const downloadStatus = await handleCandidateDownload(type, query);
    if (downloadStatus == 'success') setNotification(success('Downloaded candidates'));
    else setNotification(warning('Something went wrong, Please try again.'));
  };
  const setDownloadSource = (downloadSource: string, index: number) =>
    (candidatesData.list[index].downloadSource = downloadSource);
  const onCVClick = (index: number) => {
    updateFromPage('');
    setCvViewSourcePage(ITrackCvPages.PIPELINE);
    setCvIndex(index);
    // openModal(candidatesData.list[index]);
  };

  const addSelectedCandidate = (candidate: ICandidate & { isSelected: boolean }) => {
    if (!candidate.isSelected) setNumberOfSelectedCandidates(numberOfSelectedCandidates + 1);
    else setNumberOfSelectedCandidates(numberOfSelectedCandidates - 1);
    candidate.isSelected = !candidate.isSelected;
  };

  const selectAllCandidates = () => {
    candidatesData.list
      .filter((candidate) => candidate.email && !candidate.isInDnc)
      .forEach((candidate) => {
        candidate.isSelected = true;
      });
    setNumberOfSelectedCandidates(
      candidatesData.list.filter((candidate) => candidate.email && candidate.isSelected).length
    );
  };

  const unselectAllCandidates = () => {
    candidatesData.list.forEach((candidate) => {
      candidate.isSelected = false;
    });
    setNumberOfSelectedCandidates(0);
  };

  const handleOpenSingleEmail = (candidate: ICandidate, email: string) => {
    updateFromPage('');
    openModal(candidate);
    setSelectedEmail(email);
  };
  const onSort = (event: SelectChangeEvent) => {
    query.sort = [event.target.value ?? 'createdOn,desc'];
    setSearchParams({ d: getEncodedQueryParams({ ...query }) });
    setQuery({ ...query });
  };

  const handleSearchScope = (value) => {
    setSearchParams({ d: getEncodedQueryParams({ ...query, searchScope: value }) });
    setQuery({ ...query, searchScope: value });
    setSearchFilterDefaultValues(
      setDefaultCandidateSearchFilters({ ...defaultQuery, searchScope: value })
    );
  };

  const clearAdvanceFilters = () => {
    setAppliedFilters([]);
    setSearchParams({ d: getEncodedQueryParams(defaultQuery) });
    setSearchFilterDefaultValues(setDefaultCandidateSearchFilters(defaultQuery));
    setAdvancedFilterFields(setDefaultCandidateAdvanceFilters(defaultQuery));
    setQuery({ ...defaultQuery });
  };

  const openShare = (candidate: ICandidate & { isSelected: boolean }) => {
    setOpenShareCandidateEmail(true);
    setCandidateToShare(candidate);
    setSelectedCandidateEntitys([
      candidatesData.stageStatus[candidatesData.list.indexOf(candidate)],
    ]);
  };

  return (
    <>
      <LayoutTheme>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <AppLayoutTheme>
            <WsTitleBanner
              title="Mappings"
              goBack={() => navigateBack('/')}
              rightChildren={
                <Grid item xs={6}>
                  <Box sx={{ float: 'right' }}>
                    <Tooltip title=" Tour Guide">
                      <IconButton onClick={() => tour?.start()}>
                        <InfoIcon style={{ color: WsColors.White, fontSize: 37 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              }
            />
          </AppLayoutTheme>
        </ErrorBoundary>
        <Box>
          <Grid container>
            <Grid item xs={3}>
              <TalentPoolListSideNav selectedIndexValue={selectedIndex} />
            </Grid>
            <Grid item xs={9} my={2}>
              <MappingsSearch
                onSort={onSort}
                onSearch={(data) => {
                  amplitude('Pipeline_Search', {
                    clickedOn: 'Search',
                    workspaceName: workspace.name,
                    pageName: 'Pipeline',
                    planType: workspace.planType,
                    interfaceType: 'web',
                    pageUrl: window.location.href,
                  });
                  search(data);
                }}
                onApplyFilters={onApplyFilters}
                handleDownload={handleDownload}
                searchFilterdefaultValues={searchFilterDefaultValues}
                adavnceFilterdefaultValues={advancedFilterDefaultValues}
                clearAdvanceFilters={clearAdvanceFilters}
                onSearchScope={handleSearchScope}
              />
              <ActionsFiltersWithTitle
                recruiterActions={query.recruiterActions ?? []}
                candidateResponses={query.candidateResponses ?? []}
              />
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
              </ErrorBoundary>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                {candidatesData.list.length > 0 && (
                  <Box mt={2}>
                    <WsPagination
                      count={Math.ceil(
                        candidatesData.pagesCount / Number(paginationSettings.candidatePagination)
                      )}
                      onChange={pageChange}
                      itemsPerPage={Number(paginationSettings.candidatePagination)}
                      pageNumber={query.page}
                      defaultPage={pageNumber}
                      isPageCount={true}
                      type="Candidates"
                      totalCount={candidatesData.pagesCount}
                    />
                  </Box>
                )}
              </ErrorBoundary>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                {candidatesData.list.length > 0 && !loader && !candidatesData.isError ? (
                  <Box>
                    {candidatesData.list?.map((candidate, index) => (
                      <Box my={2} key={index}>
                        <MappingCandidateCard
                          candidate={candidate}
                          downloadSource={
                            window.location.pathname.includes('similarJobs')
                              ? 'SIMILAR_JOBS'
                              : 'PIPELINE_SEARCH'
                          }
                          isSelected={candidate.isSelected}
                          checkBoxOnclick={() => addSelectedCandidate(candidate)}
                          candidateIndex={index}
                          setDownloadSource={setDownloadSource}
                          onCVClick={onCVClick}
                          onActionClick={(index) => setCvIndex(index)}
                          openSingleEmail={handleOpenSingleEmail}
                          interaction={getInteraction(candidatesData.stageStatus, candidate.email)}
                          queryRefresh={() => setQuery({ ...query })}
                          onOpenShareIcon={() => openShare(candidate)}
                          array={
                            candidatesData.list?.map(
                              (candidate) => candidate.candidateId ?? ''
                            ) ?? ['']
                          }
                        />
                      </Box>
                    ))}
                    <Grid item xs={12} container justifyContent="flex-end" my={2}>
                      {candidatesData.pagesCount > 0 && (
                        <WsPagination
                          count={Math.ceil(
                            candidatesData.pagesCount /
                              Number(paginationSettings.candidatePagination)
                          )}
                          onChange={pageChange}
                          itemsPerPage={Number(paginationSettings.candidatePagination)}
                          pageNumber={query.page}
                          defaultPage={pageNumber}
                          isPageCount={false}
                          type="Candidates"
                          totalCount={candidatesData.pagesCount}
                        />
                      )}
                    </Grid>
                  </Box>
                ) : loader ? (
                  <WsListingLoader />
                ) : candidatesData.isError ? (
                  <Box mx={2}>
                    <NoDataFoundCard text={Pipeline_Implementation_message} />
                  </Box>
                ) : (
                  <Grid sx={{ backgroundColor: WsColors.White }} justifyContent="center">
                    {/* <NoDataFoundUploadCandidate /> */}
                    <NoDataFoundCard text={Pipeline_Implementation_message} />
                  </Grid>
                )}
              </ErrorBoundary>
            </Grid>
          </Grid>
        </Box>
      </LayoutTheme>
      {openShareCandidateEmail && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <ShareCandidateProfileEmail
            selectedCandidateEntitys={selectedCandidateEntitys}
            candidatesList={
              candidateToShare != null
                ? [candidateToShare]
                : candidatesData.list.filter(
                    (candidate) => candidate.isSelected && !candidate.isInDnc
                  )
            }
            open={openShareCandidateEmail}
            handleClose={() => {
              setOpenShareCandidateEmail(false);
              unselectAllCandidates();
            }}
          />
        </ErrorBoundary>
      )}
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {numberOfSelectedCandidates > 0 && (
          <CandidateBulkSelectionBar
            selectedCandidates={numberOfSelectedCandidates}
            selectAllCandidates={selectAllCandidates}
            unselectAllCandidates={unselectAllCandidates}
            openEmail={() => {
              updateFromPage('');
              setOpenEmail(true);
            }}
            openBulkSms={() => setOpenBulkSms(true)}
            onBulkSubmitClick={() => {
              setSubmissionSettings();
              setOpenSubmitCandidate(true);
            }}
          />
        )}
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <BulkSmsDrawer
          open={openBulkSms}
          handleClose={() => {
            setOpenBulkSms(false);
            handleSelectedCandidatesLimit(candidatesData);
            setNumberOfSelectedCandidates(0);
          }}
          candidates={candidatesData.list.filter((candidate) => candidate.isSelected)}
        />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <BulkEmail
          open={openEmail}
          ignoreDuplicateCheck={true}
          handleClose={() => {
            setOpenEmail(false);
            candidatesData.list
              .filter((candidate) => candidate.isSelected && !candidate.isInDnc)
              .map((candidate) => (candidate.isSelected = false));
            setNumberOfSelectedCandidates(0);
          }}
          candidates={candidatesData.list.filter(
            (candidate) =>
              candidate.email &&
              candidate.isSelected &&
              !candidate.isInDnc &&
              (candidate.downloadSource = window.location.pathname.includes('similarJobs')
                ? 'SIMILAR_JOBS'
                : 'PIPELINE_SEARCH')
          )}
          drawer={true}
        />
      </ErrorBoundary>
      {openSubmitCandidate && submissionSetting && (
        <CandidateBulkSubmissions
          candidatesArray={candidatesData.list.filter(
            (candidate) => candidate.isSelected && candidate.email && !candidate.isInDnc
          )}
          job={job ?? selectedJob}
          close={() => {
            setOpenSubmitCandidate(false);
            unselectAllCandidates();
          }}
          openCandidateBulkSubmissions={openSubmitCandidate}
          submissionSetting={submissionSetting}
        />
      )}
    </>
  );
};

export default MappingListing;
